import React from 'react';
import { Typography, Box, Card, CardContent, CircularProgress, alpha } from '@mui/material';

interface StatisticsCardProps {
    title: string;
    value: number | string | null;
    loading: boolean;
    color?: string;
    icon?: React.ReactNode;
    subtitle?: string;
    onClick?: () => void;
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({
    title,
    value,
    loading,
    color = 'primary.main',
    icon,
    subtitle,
    onClick
}) => {
    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 2,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': onClick ? {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    cursor: 'pointer'
                } : {},
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: color,
                }
            }}
            onClick={onClick}
        >
            <CardContent sx={{
                flexGrow: 1,
                p: 3,
                bgcolor: (theme) => alpha(theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.common.white, 0.9)
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" color="text.secondary" fontWeight={500}>
                        {title}
                    </Typography>
                    {icon && (
                        <Box sx={{
                            color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: (theme) => alpha(color, 0.1),
                            borderRadius: '50%',
                            width: 40,
                            height: 40
                        }}>
                            {icon}
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60px' }}>
                        <CircularProgress size={32} color="inherit" />
                    </Box>
                ) : (
                    <>
                        <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color }}>
                            {value !== null ? value : '-'}
                        </Typography>

                        {subtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default StatisticsCard;
